/**
 * PostHog EU browser runtime: lazy-loaded, consent-aware, SPA-aware.
 *
 * - `posthog-js` is a dynamic import that only starts after the window `load`
 *   event AND an idle callback, so it never competes with LCP or adds to TBT
 *   during the critical render. Calls made before it loaded are queued
 *   (bounded) and replayed in order.
 * - Everything goes through the same-origin reverse proxy `/ingest` (Nitro
 *   route rules), never to a posthog.com host directly.
 * - The consent mode (see `consent-policy.ts`) decides persistence, replay and
 *   identity. Mode changes are applied live with `opt_in_capturing` /
 *   `opt_out_capturing` / `set_config`, no reload needed.
 * - Only production hosts load analytics; any other host needs the explicit
 *   debug switch (`?ph_debug=1` or the `ae_ph_debug=1` cookie).
 *
 * Never throws: analytics must never break the product.
 */
import type { PostHogInterface as PostHog } from 'posthog-js'
import type {
  AnalyticsPersonProperties,
  FirstTouchProperties,
} from './events'
import {
  buildPostHogInitOptions,
  consentTransition,
  resolveAnalyticsEnvironment,
  sanitizeUrl,
} from './consent-policy'
import type { AnalyticsEnvironment, AnalyticsMode } from './consent-policy'
import { POSTHOG_PUBLIC_KEY } from './constants'
import { afterLoadAndIdle } from '../runtime/after-load-idle'
import { loadPortfolioTracker, portfolioIdentify, unloadPortfolioTracker } from './portfolio'

type Task = (ph: PostHog) => void
/** posthog-js accepts options here; the shared interface type omits them. */
type OptInCapable = {
  opt_in_capturing: (options?: { captureEventName?: string | null }) => void
}

const MAX_QUEUE = 200
const MAX_EARLY_ERRORS = 20
/** localStorage key for first-touch attribution (written ONLY with consent). */
export const FIRST_TOUCH_STORAGE_KEY = 'ae_first_touch_v1'

const posthogKey: string =
  (import.meta.env.VITE_POSTHOG_KEY as string | undefined) || POSTHOG_PUBLIC_KEY
/** Deployed commit (vite `define`), same id as the source-map release. */
export const APP_RELEASE: string =
  (import.meta.env.VITE_APP_RELEASE as string | undefined) || 'unknown'

let instance: PostHog | null = null
let loadStarted = false
let environment: AnalyticsEnvironment | null = null
let appliedMode: AnalyticsMode | null = null
let desiredMode: AnalyticsMode = 'cookieless'
const queue: Array<Task> = []
const earlyErrors: Array<unknown> = []
let removeEarlyErrorListeners: (() => void) | null = null
let pendingUser: { id: string; props: AnalyticsPersonProperties } | null = null
let identifiedId: string | null = null
let lastIdentifySignature = ''
let landingFirstTouch: FirstTouchProperties | null = null

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function run(task: Task) {
  if (instance) {
    try {
      task(instance)
    } catch {
      // never break the product
    }
    return
  }
  if (!environment?.enabled) return
  if (queue.length < MAX_QUEUE) queue.push(task)
}

/** Build first-touch attribution from the landing URL of this page load. */
export function computeFirstTouch(input: {
  href: string
  referrer: string
  locale: string
}): FirstTouchProperties {
  let pathname = '/'
  let params = new URLSearchParams()
  try {
    const url = new URL(input.href)
    pathname = url.pathname
    params = url.searchParams
  } catch {
    // keep defaults
  }
  let referringDomain = '$direct'
  const referrer = input.referrer ? sanitizeUrl(input.referrer) : '$direct'
  if (input.referrer) {
    try {
      referringDomain = new URL(input.referrer).hostname || '$direct'
    } catch {
      referringDomain = '$direct'
    }
  }
  const touch: FirstTouchProperties = {
    first_landing_page: pathname,
    first_referrer: referrer,
    first_referring_domain: referringDomain,
    first_locale: input.locale,
  }
  for (const key of ['source', 'medium', 'campaign', 'content', 'term'] as const) {
    const value = params.get(`utm_${key}`)
    if (value) touch[`first_utm_${key}`] = value.slice(0, 200)
  }
  return touch
}

function currentLocale(): string {
  return document.documentElement.lang || 'en'
}

/** First touch: persisted (consent only) value wins over this page load's. */
function firstTouchForIdentify(): FirstTouchProperties | null {
  try {
    const stored = window.localStorage.getItem(FIRST_TOUCH_STORAGE_KEY)
    if (stored) return JSON.parse(stored) as FirstTouchProperties
  } catch {
    // ignore
  }
  return landingFirstTouch
}

function persistFirstTouch() {
  if (!landingFirstTouch) return
  try {
    if (!window.localStorage.getItem(FIRST_TOUCH_STORAGE_KEY)) {
      window.localStorage.setItem(
        FIRST_TOUCH_STORAGE_KEY,
        JSON.stringify(landingFirstTouch),
      )
    }
  } catch {
    // storage blocked: first touch stays in memory for this page load
  }
}

function clearFirstTouch() {
  try {
    window.localStorage.removeItem(FIRST_TOUCH_STORAGE_KEY)
  } catch {
    // ignore
  }
}

/**
 * Remove every trace posthog-js can leave on the device for `token`: its
 * persistence (`ph_<token>_posthog`), the per-tab window id that lives in
 * sessionStorage OUTSIDE its persistence (not removed by `opt_out_capturing`),
 * and its own consent cookie. Cookies are expired on the host and on the
 * parent domain (posthog uses cross-subdomain cookies by default).
 */
export function purgePostHogStorage(token: string): void {
  const matches = (key: string) =>
    key.startsWith(`ph_${token}`) || key.startsWith(`__ph_opt_in_out_${token}`)
  for (const storage of [window.localStorage, window.sessionStorage]) {
    try {
      const keys: Array<string> = []
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i)
        if (key && matches(key)) keys.push(key)
      }
      for (const key of keys) storage.removeItem(key)
    } catch {
      // storage blocked
    }
  }
  try {
    const host = window.location.hostname
    const parent = host.split('.').slice(-2).join('.')
    for (const cookie of document.cookie.split(';')) {
      const name = cookie.split('=')[0]?.trim()
      if (!name || !matches(name)) continue
      document.cookie = `${name}=; Max-Age=0; Path=/`
      document.cookie = `${name}=; Max-Age=0; Path=/; Domain=.${parent}`
    }
  } catch {
    // ignore
  }
}

function doIdentify(ph: PostHog) {
  if (appliedMode !== 'granted' || !pendingUser) return
  const signature = JSON.stringify(pendingUser)
  if (signature === lastIdentifySignature && identifiedId === pendingUser.id) return
  lastIdentifySignature = signature
  // identify() merges the anonymous (consented) visitor into the Clerk user.
  ph.identify(
    pendingUser.id,
    pendingUser.props,
    firstTouchForIdentify() ?? undefined,
  )
  identifiedId = pendingUser.id
}

function applyMode(ph: PostHog, next: AnalyticsMode) {
  // A stale SDK opt-in (consent withdrawn on a page where the SDK had not
  // loaded yet) must not outlive our consent cookie: treat it as granted so
  // the full withdrawal runs.
  const previous =
    appliedMode === null &&
    next === 'cookieless' &&
    ph.get_explicit_consent_status() === 'granted'
      ? 'granted'
      : appliedMode
  const actions = consentTransition(previous, next)
  appliedMode = next
  for (const action of actions) {
    try {
      switch (action) {
        case 'opt_in':
          if (ph.get_explicit_consent_status() !== 'granted') {
            // No `$opt_in` event: the consent itself is not product analytics.
            ;(ph as unknown as OptInCapable).opt_in_capturing({
              captureEventName: null,
            })
          }
          break
        case 'start_replay':
          ph.set_config({ disable_session_recording: false })
          ph.startSessionRecording()
          break
        case 'identify_pending_user':
          lastIdentifySignature = ''
          doIdentify(ph)
          break
        case 'set_first_touch':
          persistFirstTouch()
          break
        case 'load_portfolio':
          loadPortfolioTracker()
          if (pendingUser) portfolioIdentify(pendingUser.id)
          break
        case 'stop_replay':
          ph.stopSessionRecording()
          ph.set_config({ disable_session_recording: true })
          break
        case 'opt_out':
          // Purges PostHog cookies + localStorage and re-enters cookieless.
          ph.opt_out_capturing()
          identifiedId = null
          lastIdentifySignature = ''
          break
        case 'clear_consent_record':
          // Remove PostHog's own opt-out cookie too: our consent cookie is the
          // record; pending + opt_out_capturing_by_default stays cookieless.
          ph.clear_opt_in_out_capturing()
          purgePostHogStorage(posthogKey)
          clearFirstTouch()
          break
        case 'unload_portfolio':
          unloadPortfolioTracker()
          break
      }
    } catch {
      // never break the product
    }
  }
}

function installEarlyErrorBuffer() {
  const onError = (event: ErrorEvent) => {
    if (earlyErrors.length < MAX_EARLY_ERRORS) earlyErrors.push(event.error ?? event.message)
  }
  const onRejection = (event: PromiseRejectionEvent) => {
    if (earlyErrors.length < MAX_EARLY_ERRORS) earlyErrors.push(event.reason)
  }
  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)
  removeEarlyErrorListeners = () => {
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
  }
}

async function loadPostHog() {
  const env = environment
  if (!env?.enabled) return
  try {
    const { default: posthog } = await import('posthog-js')
    posthog.init(posthogKey, {
      ...buildPostHogInitOptions({ mode: desiredMode }),
      before_send: (event) => {
        const props = event.properties as Record<string, unknown>
        for (const key of ['$current_url', '$referrer', '$initial_referrer', '$prev_pageview_url']) {
          const value = props[key]
          if (typeof value === 'string') props[key] = sanitizeUrl(value)
        }
        // Site language of the page (en/de/fr/it), for locale breakdowns.
        props.site_locale = currentLocale()
        // Release attribution: which deployed build produced the event.
        props.app_release = APP_RELEASE
        if (env.debug) props.environment = 'debug'
        return event
      },
      loaded: (ph) => {
        instance = ph
        // Apply the consent mode BEFORE the initial pageview is captured.
        applyMode(ph, desiredMode)
      },
    })
    instance = posthog
    if (appliedMode === null) applyMode(posthog, desiredMode)
    removeEarlyErrorListeners?.()
    removeEarlyErrorListeners = null
    for (const error of earlyErrors.splice(0)) {
      try {
        posthog.captureException(error, { early_error: true })
      } catch {
        // ignore
      }
    }
    for (const task of queue.splice(0)) {
      try {
        task(posthog)
      } catch {
        // ignore
      }
    }
  } catch {
    // Blocked or failed to load: analytics stays off for this page view.
    queue.length = 0
  }
}

/**
 * Start the runtime once per page load (idempotent). Decides the environment
 * immediately, records the landing first touch in memory, and schedules the
 * SDK load after `load` + idle.
 */
export function startAnalyticsRuntime(initialMode: AnalyticsMode): void {
  if (!isBrowser() || loadStarted) return
  loadStarted = true
  desiredMode = initialMode
  environment = resolveAnalyticsEnvironment({
    hostname: window.location.hostname,
    search: window.location.search,
    cookie: document.cookie,
  })
  landingFirstTouch = computeFirstTouch({
    href: window.location.href,
    referrer: document.referrer,
    locale: currentLocale(),
  })
  if (!environment.enabled) return
  // The Portfolio tracker follows the same consent (it uses web storage) and
  // the same environment gate (no dev/test traffic in that dashboard either).
  if (initialMode === 'granted') loadPortfolioTracker()
  installEarlyErrorBuffer()
  afterLoadAndIdle(() => {
    void loadPostHog()
  })
}

/** React to a consent change live (banner, settings, withdrawal). */
export function setAnalyticsMode(next: AnalyticsMode): void {
  if (!isBrowser()) return
  desiredMode = next
  if (instance) {
    applyMode(instance, next)
    return
  }
  // SDK not loaded (or disabled on this host): still honour the portfolio part.
  if (next === 'granted') {
    if (!environment?.enabled) return
    loadPortfolioTracker()
    if (pendingUser) portfolioIdentify(pendingUser.id)
  } else {
    unloadPortfolioTracker()
    purgePostHogStorage(posthogKey)
    clearFirstTouch()
  }
}

/** Capture a custom event (queued until the SDK has loaded). */
export function captureToPostHog(name: string, props: Record<string, unknown>): void {
  if (!isBrowser()) return
  run((ph) => {
    ph.capture(name, props)
  })
}

/** Report a handled exception (error boundaries). */
export function captureAnalyticsException(error: unknown, props?: Record<string, unknown>): void {
  if (!isBrowser()) return
  run((ph) => {
    ph.captureException(error, props)
  })
}

/**
 * The signed-in Clerk user and their (non-PII) person properties. Identifies
 * immediately with consent; otherwise remembered and applied on opt-in.
 */
export function identifyAnalyticsUser(id: string, props: AnalyticsPersonProperties): void {
  if (!isBrowser()) return
  pendingUser = { id, props }
  if (desiredMode === 'granted') portfolioIdentify(id)
  run((ph) => doIdentify(ph))
}

/** Sign-out: forget the user and rotate the anonymous id. */
export function resetAnalyticsUser(): void {
  if (!isBrowser()) return
  const wasIdentified = pendingUser !== null || identifiedId !== null
  pendingUser = null
  lastIdentifySignature = ''
  if (!wasIdentified) return
  run((ph) => {
    if (identifiedId === null) return
    identifiedId = null
    // reset() also clears PostHog's stored consent (back to the cookieless
    // default), so a visitor who still consents is opted in again right away.
    ph.reset()
    if (desiredMode === 'granted') {
      appliedMode = 'cookieless'
      applyMode(ph, 'granted')
    }
  })
}

/** Test-only: reset module state. */
export function __resetAnalyticsRuntimeForTests(): void {
  instance = null
  loadStarted = false
  environment = null
  appliedMode = null
  desiredMode = 'cookieless'
  queue.length = 0
  earlyErrors.length = 0
  removeEarlyErrorListeners?.()
  removeEarlyErrorListeners = null
  pendingUser = null
  identifiedId = null
  lastIdentifySignature = ''
  landingFirstTouch = null
}

/** Test-only: inject a fake SDK instance. */
export function __setPostHogInstanceForTests(ph: PostHog | null, mode: AnalyticsMode | null = null): void {
  instance = ph
  appliedMode = mode
  environment = { enabled: true, debug: true }
}
